// components/cashSession/OpenSessionModal.js
import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Lock, DollarSign, AlertCircle } from 'lucide-react';
import { cashSessionService } from '../../services/Cashsessionservice';

/**
 * Modal d'ouverture de session de caisse.
 * Bloque l'accès au POS tant que le vendeur n'a pas ouvert une session.
 *
 * Props:
 * - show: boolean
 * - onClose: () => void (utilisé uniquement si onSessionOpened est appelé après succès)
 * - onSessionOpened: (session) => void
 * - canCancel: boolean (default: false) — si true, le user peut fermer le modal (utile pour le manager)
 */
const OpenSessionModal = ({ show, onClose, onSessionOpened, canCancel = false }) => {
  const [openingAmount, setOpeningAmount] = useState('');
  const [openingNote, setOpeningNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setError(null);

    // Validation
    const amount = parseFloat(openingAmount);
    if (isNaN(amount) || amount < 0) {
      setError('Veuillez saisir un montant valide (≥ 0)');
      return;
    }

    setLoading(true);
    try {
      const response = await cashSessionService.openSession(amount, openingNote);

      if (response.success && response.data) {
        // Reset form
        setOpeningAmount('');
        setOpeningNote('');
        // Callback parent
        onSessionOpened?.(response.data);
      } else {
        setError(response.message || 'Erreur lors de l\'ouverture de la session');
      }
    } catch (e) {
      setError(e.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={canCancel ? onClose : undefined}
      backdrop={canCancel ? true : 'static'}
      keyboard={canCancel}
      centered
      size="md"
    >
      <Modal.Header closeButton={canCancel}>
        <Modal.Title className="d-flex align-items-center gap-2">
          <Lock size={20} className="text-success" />
          Ouvrir ma session
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="info" className="d-flex align-items-start gap-2 mb-3">
          <AlertCircle size={20} className="flex-shrink-0 mt-1" />
          <div>
            <strong>Avant de commencer à vendre :</strong>
            <p className="mb-0 small">
              Comptez le fond de caisse présent dans votre tiroir et saisissez-le ci-dessous.
              Une fois ouverte, toutes vos ventes seront rattachées à cette session.
            </p>
          </div>
        </Alert>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              <DollarSign size={16} className="me-1" />
              Montant du fond de caisse <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="any"
              placeholder="Ex: 25000"
              value={openingAmount}
              onChange={(e) => setOpeningAmount(e.target.value)}
              onWheel={(e) => e.target.blur()}
              size="lg"
              autoFocus
              disabled={loading}
            />
            <Form.Text className="text-muted">
              Montant en FCFA présent dans votre caisse à l'ouverture
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Note (optionnel)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              maxLength={500}
              placeholder="Ex: Manque 1000 FCFA reportés de la veille..."
              value={openingNote}
              onChange={(e) => setOpeningNote(e.target.value)}
              disabled={loading}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        {canCancel && (
          <Button variant="outline-secondary" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
        )}
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={loading || !openingAmount}
        >
          {loading ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Ouverture...
            </>
          ) : (
            <>
              <Lock size={16} className="me-2" />
              Ouvrir la session
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OpenSessionModal;